<template>
  <div>
    <v-menu rounded="xl">
      <template v-slot:activator="{ attrs, on }">
        <v-btn
          :color="$vuetify.breakpoint.mdAndDown ? 'accent' : 'accent'"
          class="text-capitalize"
          :class="{ 'rounded-0 rounded-l-pill': $vuetify.breakpoint.mdAndDown }"
          rounded
          depressed
          v-bind="attrs"
          v-on="on"
        >
          <v-icon left>
            {{ icons.mdiCalendar }}
          </v-icon>
          {{ selectedRangeText }}
          <v-icon right v-if="!$vuetify.breakpoint.mdAndDown">
            {{ icons.mdiMenuDown }}
          </v-icon>
        </v-btn>
      </template>

      <v-list color="accent">
        <v-list-item-group v-model="selectedRange" color="primary" mandatory>
          <v-list-item
            v-for="(date, index) in dateRange"
            :key="index"
            @click="handleDateRangeClick(date.value)"
            link
          >
            <v-list-item-title class="white--text">{{ date.text }}</v-list-item-title>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </v-menu>

    <!-- Özel Tarih Aralığı Dialog -->
    <v-dialog v-model="customRangeDialog" max-width="500px">
      <v-card>
        <v-card-title>Özel Tarih Aralığı</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6">
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="customStartDate"
                    label="Başlangıç Tarihi"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    filled
                    outlined
                    dense
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="customStartDate"
                  :min="minDate"
                  :max="maxDate"
                  @change="startDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6">
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="customEndDate"
                    label="Bitiş Tarihi"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    filled
                    outlined
                    dense
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="customEndDate"
                  :min="customStartDate"
                  :max="maxDate"
                  @change="endDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" text @click="customRangeDialog = false">İptal</v-btn>
          <v-btn color="primary" text @click="applyCustomRange">Uygula</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, watch, computed } from "vue-demi";

// Pinia
import { usePageStore } from "@/stores/pageHeader";

// Plugin
import moment from "moment";

// Icons
import { mdiCalendar, mdiMenuDown } from "@mdi/js";
export default {
  setup() {
    const pageStore = usePageStore();

    // Özel tarih aralığı için değişkenler
    const customRangeDialog = ref(false);
    const startDateMenu = ref(false);
    const endDateMenu = ref(false);
    const customStartDate = ref(moment().subtract(1, "month").format("YYYY-MM-DD"));
    const customEndDate = ref(moment().format("YYYY-MM-DD"));

    // Minimum ve maksimum tarihler (2 yıl öncesi - bugün)
    const minDate = computed(() => moment().subtract(2, "years").format("YYYY-MM-DD"));
    const maxDate = computed(() => moment().format("YYYY-MM-DD"));

    const dateRange = ref([
      {
        text: "Özel",
        value: "custom",
        start: null,
        end: null,
      },
      {
        text: "Bugün",
        value: "today",
        start: moment().format("YYYY-MM-DD"),
        end: moment().format("YYYY-MM-DD"),
      },
      {
        text: "Dün",
        value: "yesterday",
        start: moment().subtract(1, "days").format("YYYY-MM-DD"),
        end: moment().subtract(1, "days").format("YYYY-MM-DD"),
      },
      {
        text: "Bu Hafta",
        value: "thisWeek",
        start: moment().subtract(0, "weeks").startOf("week").format("YYYY-MM-DD"),
        end: moment().subtract(0, "weeks").endOf("week").format("YYYY-MM-DD"),
      },
      {
        text: "Geçen Hafta",
        value: "lastWeek",
        start: moment().subtract(1, "weeks").startOf("week").format("YYYY-MM-DD"),
        end: moment().subtract(1, "weeks").endOf("week").format("YYYY-MM-DD"),
      },
      {
        text: "Bu Ay",
        value: "thisMonth",
        start: moment().subtract(0, "month").startOf("month").format("YYYY-MM-DD"),
        end: moment().subtract(0, "month").endOf("month").format("YYYY-MM-DD"),
      },
      {
        text: "Geçen Ay",
        value: "lastMonth",
        start: moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
        end: moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      },
      {
        text: "Son 3 Ay",
        value: "last3Months",
        start: moment().subtract(3, "month").endOf("month").format("YYYY-MM-DD"),
        end: moment().format("YYYY-MM-DD"),
      },
      {
        text: "Son 6 Ay",
        value: "last6Months",
        start: moment().subtract(6, "month").endOf("month").format("YYYY-MM-DD"),
        end: moment().format("YYYY-MM-DD"),
      },
      {
        text: "Bu Yıl",
        value: "thisYear",
        start: moment().year() + "-01-01",
        end: moment().format("YYYY-MM-DD"),
      },
      {
        text: "Geçen Yıl",
        value: "thisYear",
        start: moment().year() - 1 + "-01-01",
        end: moment().year() - 1 + "-12-31",
      },
    ]);

    const selectedRange = ref(4);
    if (pageStore.hasDateRangeAll) {
      dateRange.value.unshift({
        text: "Tümü",
        value: "all",
        start: null,
        end: null,
      });
      selectedRange.value = 0;
    }

    // Özel tarih aralığı işlemleri için fonksiyonlar
    const handleDateRangeClick = (value) => {
      if (value === "custom") {
        customRangeDialog.value = true;
      }
    };

    const applyCustomRange = () => {
      // Seçilen aralık özel olarak belirlenir
      let customIndex = pageStore.hasDateRangeAll ? 1 : 0;

      // Tarih aralığını güncelle
      dateRange.value[customIndex].start = customStartDate.value;
      dateRange.value[customIndex].end = customEndDate.value;

      // Özel aralığı seç
      selectedRange.value = customIndex;

      // Store'da tarihleri güncelle
      pageStore.dateRange = {
        text: "Özel",
        value: "custom",
        start: customStartDate.value,
        end: customEndDate.value,
      };

      // Dialog'u kapat
      customRangeDialog.value = false;
    };

    const selectedRangeText = computed(() => {
      if (dateRange.value) {
        return dateRange.value[selectedRange.value].text;
      } else {
        return "-";
      }
    });

    watch(selectedRange, (val) => {
      // Özel aralık seçilmediyse normal işlem yapılır
      if (dateRange.value[val].value !== "custom" || dateRange.value[val].start !== null) {
        pageStore.dateRange = dateRange.value[val];
      }
    });

    return {
      dateRange,
      selectedRange,
      selectedRangeText,
      customRangeDialog,
      startDateMenu,
      endDateMenu,
      customStartDate,
      customEndDate,
      minDate,
      maxDate,
      handleDateRangeClick,
      applyCustomRange,
      icons: {
        mdiCalendar,
        mdiMenuDown,
      },
    };
  },
};
</script>
